import { useMemo } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { months, projectRawTotal, projectWeightedTotal } from "../../lib/calc";
import { fmtMoney, fmtMonthShort, fmtDate } from "../../lib/format";
import InfoTip from "../../components/InfoTip";
import PageHeader from "../../components/PageHeader";
import { Printer } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend,
} from "recharts";

const INK = "#0A0A0A";
const SOFT = "#A3A3A3";
const INVOICE_LINE = "#4a5a7a";
const RED = "#6B2F2A";
const GREEN = "#2C4A38";

export default function MySummary() {
  const { data, rawData, scenario, viewAs } = useData();
  const { user } = useAuth();
  // Admin in view-as mode summarises the viewed principal's portfolio.
  const myPrincipalId =
    (user?.role === "admin" && viewAs) ? viewAs : (user?.principalId || null);
  const projects = (data?.projects ?? []).filter(
    (p) => !myPrincipalId || p.principalId === myPrincipalId
  );
  const m = months(data);
  const principalName =
    (rawData?.principals || []).find((pr) => pr.id === myPrincipalId)?.name
    || user?.name
    || "Portfolio";
  const scenarioLabel = scenario === "confirmed" ? "Confirmed" : "Forecast";
  const periodLabel = m.length
    ? `${fmtMonthShort(m[0])} — ${fmtMonthShort(m[m.length - 1])}`
    : "";

  // Monthly forecast (portfolio scope only, from filtered projects — the
  // dataset the current user sees is already scenario-applied).
  const forecastByMonth = useMemo(() => {
    const out = Object.fromEntries(m.map((mo) => [mo, 0]));
    for (const p of projects) {
      for (const s of p.scopes || []) {
        for (const mo of m) out[mo] += s.revenue?.[mo] ?? 0;
      }
    }
    return out;
  }, [projects, m]);

  // Monthly invoiced actuals — computed from RAW dataset so the invoiced
  // overlay shows regardless of scenario (invoiced = actuals, they exist).
  const invoicedByMonth = useMemo(() => {
    const out = Object.fromEntries(m.map((mo) => [mo, 0]));
    const anyInvMonths = new Set();
    const rawProjects = (rawData?.projects ?? []).filter(
      (p) => !myPrincipalId || p.principalId === myPrincipalId
    );
    for (const p of rawProjects) {
      for (const s of p.scopes || []) {
        for (const mo of m) {
          const v = Number(s.invoiced?.[mo]) || 0;
          if (v) { out[mo] += v; anyInvMonths.add(mo); }
        }
      }
      // Legacy single-row actuals.
      const inv = p.actualInvoiced || {};
      for (const mo of m) {
        const v = Number(inv[mo]) || 0;
        if (v) { out[mo] += v; anyInvMonths.add(mo); }
      }
    }
    return { totals: out, months: anyInvMonths };
  }, [rawData, myPrincipalId, m]);

  const monthlyChart = m.map((mo) => ({
    month: fmtMonthShort(mo),
    forecast: forecastByMonth[mo],
    invoiced: invoicedByMonth.months.has(mo) ? invoicedByMonth.totals[mo] : null,
  }));

  // Table rows for months that have any invoicing OR any forecast.
  const monthlyTable = m
    .map((mo) => {
      const f = forecastByMonth[mo] || 0;
      const has = invoicedByMonth.months.has(mo);
      const i = has ? (invoicedByMonth.totals[mo] || 0) : null;
      const variance = has ? (i - f) : null;
      return { mo, month: fmtMonthShort(mo), forecast: f, invoiced: i, variance, hasInvoice: has };
    });

  const totalRaw = projects.reduce((s, p) => s + projectRawTotal(p), 0);
  const totalWtd = projects.reduce((s, p) => s + projectWeightedTotal(p), 0);
  const invoicedYtd = m.reduce((s, mo) => s + (invoicedByMonth.totals[mo] || 0), 0);
  const byStatus = projects.reduce((acc, p) => {
    const k = p.status || "In Progress";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const byContract = projects.reduce((acc, p) => {
    acc[p.contract] = (acc[p.contract] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="my-summary">
      {/* Print-only report letterhead */}
      <div className="print-only" data-testid="print-header">
        <div style={{ borderBottom: "2px solid #000", paddingBottom: 8, marginBottom: 16 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "#555" }}>
            Monk Mackenzie · Portfolio report
          </div>
          <div style={{ fontFamily: "Chivo, sans-serif", fontWeight: 900, fontSize: 22, marginTop: 4 }}>
            Portfolio — {principalName}
          </div>
          <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>
            Report date {fmtDate(new Date().toISOString())} · Scenario: {scenarioLabel} · Period: {periodLabel}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4 no-print">
        <PageHeader
          section={user?.role === "associate" ? "PORTFOLIO · ASSOCIATE" : "PORTFOLIO"}
          title="Summary"
          subtitle="Totals and monthly split for your portfolio only."
        />
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] hover:bg-[var(--paper-soft)] font-display text-[11px] tracked-tight uppercase text-[var(--muted-fg)]"
          data-testid="print-summary-btn"
          title="Print or save as PDF (Cmd/Ctrl-P)"
        >
          <Printer size={13} /> Print
        </button>
      </div>

      <section className="border border-[var(--rule)]">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[var(--rule)]">
          <div className="kpi" data-testid="my-total-raw">
            <div className="kpi-label inline-flex items-center">
              Raw forecast
              <InfoTip side="bottom" label="Raw forecast">
                Sum of every project's monthly fees in your portfolio — no probability applied.
              </InfoTip>
            </div>
            <div className="kpi-value">{fmtMoney(totalRaw, { compact: true })}</div>
          </div>
          <div className="kpi" data-testid="my-total-weighted">
            <div className="kpi-label inline-flex items-center">
              Weighted forecast
              <InfoTip side="bottom" label="Weighted forecast">
                Each project's fees multiplied by its probability before summing. A more cautious view of likely revenue.
              </InfoTip>
            </div>
            <div className="kpi-value">{fmtMoney(totalWtd, { compact: true })}</div>
          </div>
          <div className="kpi" data-testid="my-projects-count">
            <div className="kpi-label">Active projects</div>
            <div className="kpi-value">{projects.length}</div>
          </div>
          <div className="kpi" data-testid="my-invoiced-ytd">
            <div className="kpi-label inline-flex items-center">
              Invoiced (window)
              <InfoTip side="left" label="Invoiced">
                Sum of invoiced actuals for months in the visible window on your portfolio.
              </InfoTip>
            </div>
            <div className="kpi-value">{fmtMoney(invoicedYtd, { compact: true })}</div>
          </div>
        </div>
      </section>

      <section>
        <div className="eyebrow mb-2">By month</div>
        <h3 className="section-h mb-4">Monthly revenue · forecast vs invoiced</h3>
        <div className="border border-[var(--rule)] p-4" style={{ height: 300 }} data-testid="my-monthly-chart">
          <ResponsiveContainer>
            <ComposedChart data={monthlyChart} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="myrev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={INK} stopOpacity={0.28} />
                  <stop offset="95%" stopColor={INK} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 4" stroke="#EEE" vertical={false} />
              <XAxis dataKey="month" stroke={SOFT} fontSize={11} tickLine={false} axisLine={{ stroke: "#E5E5E5" }} />
              <YAxis stroke={SOFT} fontSize={11} tickLine={false} axisLine={false}
                tickFormatter={(v) => fmtMoney(v, { compact: true })} />
              <Tooltip contentStyle={{ background:"#fff", border:"1px solid #E5E5E5", borderRadius:0, fontFamily:"IBM Plex Mono", fontSize:11 }}
                formatter={(v, n) => [fmtMoney(v), n === "invoiced" ? "Invoiced" : "Forecast"]} />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => (v === "invoiced" ? "Invoiced (actual)" : "Forecast revenue")} />
              <Area type="monotone" dataKey="forecast" stroke={INK} strokeWidth={1.5} fill="url(#myrev)" />
              <Line type="monotone" dataKey="invoiced" stroke={INVOICE_LINE} strokeWidth={1.5}
                strokeDasharray="4 4" dot={{ r: 2.5 }} connectNulls={false} data-testid="my-invoiced-line" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Compact month-by-month table with variance — always present, always
          the source of truth on paper. */}
      <section data-testid="my-monthly-table">
        <div className="eyebrow mb-2">Month by month</div>
        <div className="border border-[var(--rule)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="eyebrow border-b border-[var(--rule)]">
                <th className="px-4 py-3 text-left">Month</th>
                <th className="px-4 py-3 text-right">Forecast</th>
                <th className="px-4 py-3 text-right">Invoiced</th>
                <th className="px-4 py-3 text-right">Variance</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {monthlyTable.map((r) => {
                const isRed = r.variance != null && r.variance < 0;
                const isGreen = r.variance != null && r.variance > 0;
                return (
                  <tr key={r.mo} className="border-b border-[var(--rule)] last:border-b-0" data-testid={`my-monthly-row-${r.mo}`}>
                    <td className="px-4 py-2 text-left">{r.month}</td>
                    <td className="px-4 py-2 text-right">{fmtMoney(r.forecast)}</td>
                    <td className="px-4 py-2 text-right">{r.hasInvoice ? fmtMoney(r.invoiced) : "—"}</td>
                    <td className="px-4 py-2 text-right"
                        style={{ color: isRed ? RED : isGreen ? GREEN : undefined }}
                        data-testid={`my-monthly-variance-${r.mo}`}>
                      {r.hasInvoice
                        ? (isRed ? `−${fmtMoney(Math.abs(r.variance))}` : (isGreen ? `+${fmtMoney(r.variance)}` : fmtMoney(0)))
                        : "—"}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-[var(--rule-strong)] bg-[var(--paper-soft)]">
                <td className="px-4 py-2 text-left uppercase text-[11px] tracking-widest">Total</td>
                <td className="px-4 py-2 text-right">{fmtMoney(m.reduce((s, mo) => s + forecastByMonth[mo], 0))}</td>
                <td className="px-4 py-2 text-right">{fmtMoney(invoicedYtd)}</td>
                <td className="px-4 py-2 text-right"
                    style={{ color: (invoicedYtd - m.reduce((s, mo) => s + forecastByMonth[mo], 0)) < 0 ? RED : undefined }}>
                  {(() => {
                    const v = invoicedYtd - m.reduce((s, mo) => s + forecastByMonth[mo], 0);
                    return v < 0 ? `−${fmtMoney(Math.abs(v))}` : `+${fmtMoney(v)}`;
                  })()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="eyebrow mb-2">By status</div>
          <div className="border border-[var(--rule)]">
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(byStatus).map(([k, v]) => (
                  <tr key={k} className="border-b border-[var(--rule)] last:border-b-0">
                    <td className="px-4 py-3">{k}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{v}</td>
                  </tr>
                ))}
                {Object.keys(byStatus).length === 0 && (
                  <tr><td className="px-4 py-3 eyebrow text-[var(--soft-fg)]" colSpan={2}>No projects yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="eyebrow mb-2">By contract</div>
          <div className="border border-[var(--rule)]">
            <table className="w-full text-sm">
              <tbody>
                {Object.entries(byContract).map(([k, v]) => (
                  <tr key={k} className="border-b border-[var(--rule)] last:border-b-0">
                    <td className="px-4 py-3"><span className={`chip ${k==="Yes"?"chip-yes":k==="Tentative"?"chip-tentative":"chip-no"}`}>{k}</span></td>
                    <td className="px-4 py-3 text-right font-mono-num">{v}</td>
                  </tr>
                ))}
                {Object.keys(byContract).length === 0 && (
                  <tr><td className="px-4 py-3 eyebrow text-[var(--soft-fg)]" colSpan={2}>No projects yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Project list — prints on the same PDF so the whole portfolio's
          shape is visible on paper. */}
      <section data-testid="my-projects-list">
        <div className="eyebrow mb-2">Projects</div>
        <div className="border border-[var(--rule)]">
          <table className="w-full text-sm">
            <thead>
              <tr className="eyebrow border-b border-[var(--rule)]">
                <th className="px-4 py-3 text-left">Project</th>
                <th className="px-4 py-3 text-left">Contract</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Raw</th>
                <th className="px-4 py-3 text-right">Weighted</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-[var(--rule)] last:border-b-0">
                  <td className="px-4 py-2 text-left">{p.name}</td>
                  <td className="px-4 py-2 text-left">
                    <span className={`chip ${p.contract==="Yes"?"chip-yes":p.contract==="Tentative"?"chip-tentative":"chip-no"}`}>
                      {p.contract}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-left">{p.status || "—"}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(projectRawTotal(p))}</td>
                  <td className="px-4 py-2 text-right">{fmtMoney(projectWeightedTotal(p))}</td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr><td className="px-4 py-3 eyebrow text-[var(--soft-fg)]" colSpan={5}>No projects yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
